/**
 * Lazy SVG thumbnail of a library item (CC_05 Block 3). Fetches the item's
 * elements when it first scrolls into view and renders them via
 * `renderItemSvg` (host-side exportToSvg; no packages/ edit). Shared by the
 * /library page AND the in-board library panel — the in-board panel had no
 * preview at all in v1.3.0, which is the "Vorschau rendert nicht" defect.
 */

import { useEffect, useRef, useState } from "react";

import { ryfeApi } from "../api";
import { renderItemSvg } from "../library-preview";

import "./ryfe.scss";

export const LibraryPreview = ({
  wsId,
  itemId,
}: {
  wsId: string;
  itemId: string;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");

  useEffect(() => {
    const el = ref.current;
    if (!el || state !== "idle") {
      return;
    }
    const load = async () => {
      setState("loading");
      try {
        const item = await ryfeApi.getLibraryItem(wsId, itemId);
        const markup = await renderItemSvg(item.elements || []);
        if (markup) {
          setSvg(markup);
        } else {
          setState("error");
        }
      } catch {
        setState("error");
      }
    };
    // Lazy-load on first intersection; if IntersectionObserver is unavailable
    // (older embed contexts), load eagerly so the thumbnail never stays blank.
    if (typeof IntersectionObserver === "undefined") {
      void load();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        void load();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [wsId, itemId, state]);

  return (
    <div className="ryfe-libprev" ref={ref}>
      {svg ? (
        // eslint-disable-next-line react/no-danger
        <div
          className="ryfe-libprev__svg"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <span className="ryfe-muted">{state === "error" ? "—" : "…"}</span>
      )}
    </div>
  );
};
